import React, { Suspense, lazy, useEffect, useState } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ToastProvider } from "./context/ToastContext";
import { CardProvider } from "./context/CardContext";
import NavBar from "./components/NavBar/NavBar";
import Footer from "./components/Footer/Footer";
import ErrorBoundary from "./components/ErrorBoundary/ErrorBoundary";
import GeneratorPage from "./pages/GeneratorPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import useAntiInspect from "./hooks/useAntiInspect";
import "./App.css";

/**
 * Everything except the generator and the auth screens is split out of the
 * main bundle: the admin panel, the ticket console and the API docs are large
 * and most visitors never open them, so they should not sit in the critical
 * path of the first paint.
 */
const CardSettingsPage = lazy(() => import("./pages/CardSettingsPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const CheckoutPage = lazy(() => import("./pages/CheckoutPage"));
const HistoryPage = lazy(() => import("./pages/HistoryPage"));
const PhotosPage = lazy(() => import("./pages/PhotosPage"));
const TicketsPage = lazy(() => import("./pages/TicketsPage"));
const TicketPage = lazy(() => import("./pages/TicketPage"));
const ApiDocsPage = lazy(() => import("./pages/ApiDocsPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const StaffPage = lazy(() => import("./pages/StaffPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));

function readTheme() {
  try {
    return localStorage.getItem("foliox-theme") === "light" ? "light" : "dark";
  } catch (err) {
    return "dark";
  }
}

function Splash({ label = "Loading" }) {
  return (
    <div className="route-splash" role="status">
      <span className="spinner" aria-hidden="true" />
      {label}
    </div>
  );
}

/** Sends signed-out visitors to the login screen, remembering where they were. */
function RequireAuth({ children, role }) {
  const { isAuthed, ready, user } = useAuth();
  const location = useLocation();

  if (!ready) return <Splash label="Checking your session" />;
  if (!isAuthed) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }
  if (role === "staff" && !(user.role === "staff" || user.role === "admin")) {
    return <Navigate to="/" replace />;
  }
  if (role === "admin" && user.role !== "admin")
    return <Navigate to="/" replace />;
  return children;
}

/** Signed-in users have no reason to see the login or register screens. */
function GuestOnly({ children }) {
  const { isAuthed, ready } = useAuth();
  if (!ready) return <Splash label="Checking your session" />;
  return isAuthed ? <Navigate to="/" replace /> : children;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  // Block body on purpose: a concise arrow would return whatever scrollTo
  // gives back, and React treats an effect's return value as its cleanup
  // function - calling it on the next navigation and tearing down the app.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

/** Shown when the server reports the signed-in account is suspended or
    banned - distinct from a plain expired session, which just bounces to
    the login screen without an explanation. The account is already signed
    out by the time this appears (client.js does that before raising the
    event); this is purely explaining why. */
function BlockedModal({ info, onDismiss }) {
  const navigate = useNavigate();
  if (!info) return null;
  const banned = info.code === "account_banned";
  return (
    <div className="blocked-modal-veil" role="presentation">
      <div className="blocked-modal" role="alertdialog" aria-modal="true" aria-labelledby="blocked-title">
        <h2 id="blocked-title">{banned ? "Account banned" : "Account suspended"}</h2>
        <p>{info.message || "This account is no longer active."}</p>
        <p className="muted">You have been signed out.</p>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => {
            onDismiss();
            navigate("/login");
          }}
        >
          OK
        </button>
      </div>
    </div>
  );
}

function Shell() {
  const [theme, setTheme] = useState(readTheme);
  const { blockedInfo, clearBlockedInfo } = useAuth();
  const location = useLocation();
  // Right-click, common DevTools shortcuts, and a docked-panel size
  // heuristic - a deterrent, not a boundary; see the hook for why neither
  // this nor anything else served to a browser can really stop either.
  const devtoolsOpen = useAntiInspect();

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    try {
      localStorage.setItem("foliox-theme", theme);
    } catch (err) {
      /* storage may be blocked; the theme still applies for this session */
    }
  }, [theme]);

  return (
    <>
      <NavBar
        theme={theme}
        onToggleTheme={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}
      />
      <BlockedModal info={blockedInfo} onDismiss={clearBlockedInfo} />
      {devtoolsOpen && (
        <div className="devtools-banner" role="alert">
          Developer tools appear to be open — sensitive previews are hidden while this window is open.
        </div>
      )}
      <ScrollToTop />
      <ErrorBoundary>
        <Suspense fallback={<Splash />}>
          {/* Keying by pathname remounts this wrapper on every route change,
              which restarts the CSS fade-in below - a lightweight page
              transition with no extra dependency and no exit-animation
              machinery (see .page-fade in App.css). */}
          <div className="page-fade" key={location.pathname}>
          <Routes>
            <Route path="/" element={<GeneratorPage />} />
            <Route path="/settings" element={<CardSettingsPage />} />
          <Route path="/pricing" element={<PricingPage />} />
            <Route path="/about" element={<AboutPage />} />

            <Route
              path="/login"
              element={
                <GuestOnly>
                  <LoginPage />
                </GuestOnly>
              }
            />
            <Route
              path="/register"
              element={
                <GuestOnly>
                  <RegisterPage />
                </GuestOnly>
              }
            />

            <Route
              path="/profile"
              element={
                <RequireAuth>
                  <ProfilePage />
                </RequireAuth>
              }
            />
            <Route
              path="/checkout/:orderId"
              element={
                <RequireAuth>
                  <CheckoutPage />
                </RequireAuth>
              }
            />
            <Route
              path="/history"
              element={
                <RequireAuth>
                  <HistoryPage />
                </RequireAuth>
              }
            />
            <Route
              path="/photos"
              element={
                <RequireAuth>
                  <PhotosPage />
                </RequireAuth>
              }
            />
            <Route
              path="/support"
              element={
                <RequireAuth>
                  <TicketsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/support/:id"
              element={
                <RequireAuth>
                  <TicketPage />
                </RequireAuth>
              }
            />
            <Route
              path="/api-docs"
              element={
                <RequireAuth>
                  <ApiDocsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/staff"
              element={
                <RequireAuth role="staff">
                  <StaffPage />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/*"
              element={
                <RequireAuth role="admin">
                  <AdminPage />
                </RequireAuth>
              }
            />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </div>
        </Suspense>
      </ErrorBoundary>
      <Footer />
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <AuthProvider>
          <CardProvider>
            <Shell />
          </CardProvider>
        </AuthProvider>
      </ToastProvider>
    </BrowserRouter>
  );
}
